import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input } from '@arco-design/web-react';

import { useDiskPricingSync } from '../../components/diskPricingSync';
import { ResourceConfigAddCard, ResourceConfigCard } from '../../components/resourceConfig';
import { VendorDevicePersistFields } from '../../components/vendorDevice';
import { isFieldDisabled, isFieldReadonly } from '../../platform/capability-registry/fieldControl';
import { isActionVisible, isFieldVisible } from '../../platform/capability-registry/resolveFieldMode';
import { useScheduleResourceFormPriceRefresh } from '../../platform/resource-form';
import { SystemDiskConfigModal } from './SystemDiskConfigModal';
import {
  DEFAULT_SYSTEM_DISK_SIZE_GI,
  DEFAULT_SYSTEM_DISK_SIZE_MARK_COUNT,
  DEFAULT_SYSTEM_DISK_SIZE_MAX,
  DEFAULT_SYSTEM_DISK_SIZE_MIN,
  SYSTEM_DISK_RESOURCE_ADD_LABEL,
  SYSTEM_DISK_RESOURCE_ICON,
} from './constants';
import { SYSTEM_DISK_SIZE_ACTION_KEYS, SYSTEM_DISK_SIZE_FIELD_KEYS } from './fieldKeys';
import styles from './index.module.less';
import { resolveSystemDiskSizePaths } from './resolvePaths';
import type { SystemDiskSizeBlockProps, SystemDiskSizePaths } from './types';
import {
  applySystemDiskModalFormValues,
  buildSystemDiskModalFormValues,
  buildSystemDiskSummaryItems,
  buildSystemDiskWatchFields,
  clearSystemDiskFields,
  hasSystemDiskFormData,
  isSystemDiskConfigured,
  readSystemDiskFormStore,
} from './utils';

const FormItem = Form.Item;

/** 系统盘能力块：卡片 + Modal（容量 / 可选计价厂商设备） */
export function SystemDiskSizeBlock(props: SystemDiskSizeBlockProps) {
  const { form, paths: rawPaths, options, ...control } = props;
  const paths = useMemo((): SystemDiskSizePaths => {
    if (rawPaths?.size && rawPaths?.pricing) return rawPaths as SystemDiskSizePaths;
    return resolveSystemDiskSizePaths(rawPaths?.pathPrefix ?? '', options);
  }, [options, rawPaths]);
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);

  const min = options?.min ?? DEFAULT_SYSTEM_DISK_SIZE_MIN;
  const max = options?.max ?? DEFAULT_SYSTEM_DISK_SIZE_MAX;
  const defaultValue = options?.defaultValue ?? min ?? DEFAULT_SYSTEM_DISK_SIZE_GI;
  const markCount = options?.markCount ?? DEFAULT_SYSTEM_DISK_SIZE_MARK_COUNT;
  const translateMarkUnit = options?.translateMarkUnit === true;

  const { pricingActive } = useDiskPricingSync(options);
  const billModelId = pricingActive ? options?.billModelId : undefined;
  const deploymentTypeId = pricingActive ? options?.deploymentTypeId : undefined;
  const paymentTiming = options?.paymentTiming;
  const skuType = options?.skuType;
  const resourceEditMode = options?.resourceEditMode;
  /** 下单：SKU 预置已有系统盘时隐藏卡片删/改（自定义/后付费） */
  const lockActionsWhenFilled = options?.lockActionsWhenFilled === true;
  /** 固定规格下单：无论是否有数据均禁止增删改 */
  const isOrderFixed = resourceEditMode === 'order-fixed';

  const readonly = isFieldReadonly(SYSTEM_DISK_SIZE_FIELD_KEYS.size, control) || isOrderFixed;
  const disabled = isFieldDisabled(SYSTEM_DISK_SIZE_FIELD_KEYS.size, control);
  const locked = readonly || disabled;

  /** 容量 + 厂商叶子字段一并 watch，避免只 watch 父对象时回填不刷新 */
  const watchFields = useMemo(() => buildSystemDiskWatchFields(paths), [paths]);
  const watchedSnapshot = Form.useWatch(watchFields, form);

  const formValues = useMemo(
    () => readSystemDiskFormStore(form),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 跟随系统盘字段刷新配置态
    [form, watchedSnapshot],
  );
  const hasCardData = hasSystemDiskFormData(formValues, paths);
  /** 仅当 SKU 预置已配置系统盘时锁定；用户下单过程中新增不锁 */
  const presetHasSystemDisk = options?.presetConfigSnapshot ? hasSystemDiskFormData(options.presetConfigSnapshot, paths) : false;
  const summaryItems = useMemo(
    () => buildSystemDiskSummaryItems(form, paths, Boolean(billModelId)),
    [billModelId, form, paths, watchedSnapshot],
  );

  const modalInitialValues = useMemo(() => {
    if (!modalOpen) return undefined;
    return buildSystemDiskModalFormValues(readSystemDiskFormStore(form), paths, defaultValue);
  }, [defaultValue, form, modalOpen, paths]);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);
  const schedulePriceRefresh = useScheduleResourceFormPriceRefresh();

  const handleModalOk = useCallback(
    (modalValues: Record<string, unknown>) => {
      applySystemDiskModalFormValues(form, paths, modalValues, defaultValue);
      closeModal();
      schedulePriceRefresh(form);
    },
    [closeModal, defaultValue, form, paths, schedulePriceRefresh],
  );

  const handleRemove = useCallback(() => {
    clearSystemDiskFields(form, paths);
  }, [form, paths]);

  /** 与 Compute 卡片空态校验文案对齐：系统盘配置必填（含未选计价厂商设备） */
  const requiredRule = useMemo(
    () =>
      locked
        ? undefined
        : [
            {
              validator(_value: unknown, callback: (error?: string) => void) {
                if (!isSystemDiskConfigured(readSystemDiskFormStore(form), paths, billModelId, paymentTiming, skuType, resourceEditMode)) {
                  callback(t('cluster:required', { content: SYSTEM_DISK_RESOURCE_ADD_LABEL }));
                  return;
                }
                callback();
              },
            },
          ],
    [billModelId, form, locked, paths, paymentTiming, resourceEditMode, skuType, t],
  );

  if (!isFieldVisible(SYSTEM_DISK_SIZE_FIELD_KEYS.size, control)) {
    return null;
  }

  const actionsLockedByFill = lockActionsWhenFilled && presetHasSystemDisk;
  const canEdit = !locked && !actionsLockedByFill && isActionVisible(SYSTEM_DISK_SIZE_ACTION_KEYS.edit, control);
  const canDelete = !locked && !actionsLockedByFill && isActionVisible(SYSTEM_DISK_SIZE_ACTION_KEYS.remove, control);

  return (
    <>
      {hasCardData ? (
        <ResourceConfigCard
          resourceCode={options?.resourceCode ?? 'SystemDisk'}
          /** 厂商设备已由下方 PersistFields 常驻挂载，避免重复 FormItem */
          vendorDevicePrefix={undefined}
          icon={SYSTEM_DISK_RESOURCE_ICON}
          title={SYSTEM_DISK_RESOURCE_ADD_LABEL}
          items={summaryItems}
          canEdit={canEdit}
          canDelete={canDelete}
          onEdit={openModal}
          onDelete={handleRemove}
        />
      ) : locked ? null : (
        <FormItem shouldUpdate noStyle>
          {(_, formInstance) => {
            const hasError = Boolean(formInstance.getFieldError(paths.size)?.message);
            return (
              <ResourceConfigAddCard
                resourceCode={options?.resourceCode ?? 'SystemDisk'}
                icon={SYSTEM_DISK_RESOURCE_ICON}
                title={hasError ? `${SYSTEM_DISK_RESOURCE_ADD_LABEL}（必填）` : SYSTEM_DISK_RESOURCE_ADD_LABEL}
                hasError={hasError}
                onAdd={openModal}
              />
            );
          }}
        </FormItem>
      )}

      <div className={styles.persistFields} aria-hidden>
        <FormItem field={paths.size} noStyle rules={requiredRule}>
          <Input />
        </FormItem>
        {/* 始终挂载：编辑回填 / 弹窗写回的厂商设备才能进入 Form store 并可被 getFieldsValue 收集 */}
        <VendorDevicePersistFields fieldPrefix={paths.pricing} />
      </div>

      {modalOpen ? (
        <SystemDiskConfigModal
          billModelId={billModelId}
          deploymentTypeId={deploymentTypeId}
          paymentTiming={paymentTiming}
          skuType={skuType}
          resourceEditMode={resourceEditMode}
          resourceCode={options?.resourceCode}
          initialValues={modalInitialValues}
          minSize={min}
          maxSize={max}
          markCount={markCount}
          translateUnit={translateMarkUnit}
          onOk={handleModalOk}
          onCancel={closeModal}
        />
      ) : null}
    </>
  );
}
